import React, { useState, useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { Icon } from '@components/icons';
import { usePrefersReducedMotion } from '@hooks';

const Projects = () => {
  const data = useStaticQuery(graphql`
    query {
      projects: allMarkdownRemark(
        filter: {
          fileAbsolutePath: { regex: "/content/projects/" }
          frontmatter: { showInProjects: { ne: false } }
        }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              tech
              github
              external
              date
            }
            html
          }
        }
      }
    }
  `);

  const [showMore, setShowMore] = useState(false);
  const revealTitle = useRef(null);
  const revealArchiveLink = useRef(null);
  const revealProjects = useRef([]);
  const moreButtonRef = useRef(null);
  const showMoreButtonPosition = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }
    sr.reveal(revealTitle.current, srConfig());
    sr.reveal(revealArchiveLink.current, srConfig());
    revealProjects.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const GRID_LIMIT = 6;
  const projects = data.projects.edges.filter(({ node }) => node);
  const firstSix = projects.slice(0, GRID_LIMIT);
  const projectsToShow = showMore ? projects : firstSix;

  const handleShowMoreClick = () => {
    if (showMore) {
      const projectsList = document.querySelector('.projects-timeline');
      if (projectsList) {
        const items = projectsList.children;
        if (items.length >= 6) {
          const sixthProject = items[5];
          const rect = sixthProject.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          showMoreButtonPosition.current = rect.bottom + scrollTop + 50;
        }
      }
      setShowMore(false);
      setTimeout(() => {
        if (showMoreButtonPosition.current) {
          window.scrollTo({ top: showMoreButtonPosition.current, behavior: 'smooth' });
        }
      }, 300);
    } else {
      setShowMore(true);
    }
  };

  const projectInner = node => {
    const { frontmatter, html } = node;
    const { github, external, title, tech, date } = frontmatter;
    const year = new Date(date).getFullYear();

    return (
      <>
        <div className="project-year">{year}</div>
        <div className="project-content">
          <div className="project-header">
            {github || external ? (
              <a
                href={external || github}
                target="_blank"
                rel="noreferrer"
                className="project-link-wrapper">
                <h3 className="project-title">{title}</h3>
                <div className="project-links">
                  <div className="arrow-icon">
                    <Icon name="Arrow" />
                  </div>
                </div>
              </a>
            ) : (
              <div className="project-link-wrapper">
                <h3 className="project-title">{title}</h3>
              </div>
            )}
          </div>

          <div className="project-description" dangerouslySetInnerHTML={{ __html: html }} />

          {tech && tech.length > 0 && (
            <ul className="project-tech-list">
              {tech.map((t, i) => (
                <li key={i}>{t}</li>
              ))}
            </ul>
          )}
        </div>
      </>
    );
  };

  return (
    <section className="max-w-[1000px]">
      <h2
        className="numbered-heading"
        style={{ fontSize: 'clamp(24px, 5vw, var(--fz-heading))' }}
        ref={revealTitle}>
        Other Noteworthy Projects
      </h2>

      <a
        className="inline-link archive-link"
        href="https://github.com/M-Sarim"
        target="_blank"
        rel="noreferrer"
        ref={revealArchiveLink}>
        view all projects on GitHub
      </a>

      {/* project-timeline-item classes defined in global.css */}
      <ul className="projects-timeline">
        {prefersReducedMotion ? (
          <>
            {projectsToShow &&
              projectsToShow.map(({ node }, i) => (
                <li key={i} className="project-timeline-item">
                  {projectInner(node)}
                </li>
              ))}
          </>
        ) : (
          <TransitionGroup component={null}>
            {projectsToShow &&
              projectsToShow.map(({ node }, i) => (
                <CSSTransition
                  key={i}
                  classNames="fadeup"
                  timeout={i >= GRID_LIMIT ? (i - GRID_LIMIT) * 300 : 300}
                  exit={false}>
                  <li
                    className="project-timeline-item"
                    ref={el => (revealProjects.current[i] = el)}
                    style={{
                      transitionDelay: `${i >= GRID_LIMIT ? (i - GRID_LIMIT) * 100 : 0}ms`,
                    }}>
                    {projectInner(node)}
                  </li>
                </CSSTransition>
              ))}
          </TransitionGroup>
        )}
      </ul>

      {/* more-button class = button styles (global.css) */}
      <button className="more-button block mx-auto mt-[80px]" ref={moreButtonRef} onClick={handleShowMoreClick}>
        Show {showMore ? 'Less' : 'More'}
      </button>
    </section>
  );
};

export default Projects;
